import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { CreateTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/inputs/create-ticket.model';
import { useMemo, useState } from 'react';
import {
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetUsersPaginatedQuery } from '@service/cpms-bo-user.service';

import { TicketScope } from '../../ticket-update/use-ticket-scope.hook';

interface AssigneeSelectionComponentProps {
  field: ControllerRenderProps<CreateTicketModel, 'assigneeId'>;
  fieldState: ControllerFieldState;
  // When false, the assignee can only be replaced, not cleared: the API cannot
  // unassign a ticket.
  clearable?: boolean;
  // Customer/network of an existing ticket. Without it, they are read from the
  // creation form.
  scope?: TicketScope;
}

export const AssigneeSelectionComponent = ({
  field,
  fieldState: { error },
  clearable = true,
  scope,
}: AssigneeSelectionComponentProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { watch } = useFormContext<CreateTicketModel>();
  const [formCustomerId, formNetworkId] = watch(['customerId', 'networkId']);
  const customerId = scope ? scope.customerId : formCustomerId;
  const networkId = scope ? scope.networkId : formNetworkId;

  const { data, isSuccess } = useCpmsBoGetUsersPaginatedQuery(
    {
      limit: 100,
      offset: 0,
      ...(customerId && { customerId }),
      ...(networkId && { networkId }),
    },
    { skip: scope?.isLoading },
  );

  const selectedItem = useMemo((): AutocompleteItem | null => {
    if (!isSuccess) return null;
    const user = data.docs.find(({ id }) => id === field.value) ?? null;
    return user
      ? { key: user.id, label: `${user.firstName} ${user.lastName}` }
      : null;
  }, [data, isSuccess, field.value]);

  const autocompleteItems = useMemo((): AutocompleteItem[] => {
    if (!isSuccess) return [];
    return data.docs
      .filter(({ firstName, lastName }) =>
        `${firstName} ${lastName}`.toLowerCase().includes(search.toLowerCase()),
      )
      .map((user) => ({
        key: user.id,
        label: `${user.firstName} ${user.lastName}`,
      }));
  }, [search, data, isSuccess]);

  return (
    <Stack direction="column">
      <Text uppercase fontSize={TextFontSize.Small}>
        {t('delmonicos-shared-sdk.inputs-create-ticket.assignee-id')}
      </Text>
      <AutocompleteInputComponent
        value={search}
        onChange={setSearch}
        onSearch={setSearch}
        items={autocompleteItems}
        selectedItem={selectedItem}
        clearable={clearable}
        onSelect={(item) => {
          field.onChange(item?.key ?? undefined);
          if (!clearable) setSearch('');
        }}
        emptyResultText={t('global.no-result-found')}
        errorMessage={error?.message}
      />
    </Stack>
  );
};
